/**
 * Customize PDP helpers: cover variant grouping/labels and draft selection maps (preselect, option slots).
 */

import {resolveOptionSwatchThumbnailUrl} from '@amblique/king-living/app/components/configuration-selector/utils';
import {normalizeCylindoFeatureToString, parseProcessedImageGroups} from '@amblique/king-living/app/utils/cylindo-params';
import {
  getOptionsGroupingUIProcessed,
  getProductEffectivePrice,
  getProductListPrice,
  isMultiTypeOptionsContainer,
  resolveOptionProductName,
  resolveOptionProductShortDescriptionPlain,
  resolveShelvesPrimaryName,
} from '@amblique/king-living/app/utils/product-utils';
import {
  getPopularCoverIds,
  sortCollectionGroups,
  sortColourGroups,
  sortCoverVariantsForDisplay,
} from '@amblique/king-living/app/utils/variant-cover-sort';

// --- Cover variants & groups -------------------------------------------------

/**
 * Fallback when `swatchName` is missing (SCAPI / legacy): reads `variationValues` (often internal IDs).
 *
 * @param {Object} variationValues
 * @returns {string}
 */
export const getCoverDisplayValueFromVariationValues = (variationValues) => {
  if (!variationValues || typeof variationValues !== 'object') {
    return '';
  }

  const coverValue = variationValues.cover ?? variationValues[Object.keys(variationValues)[0]];

  return coverValue != null ? String(coverValue) : '';
};

/**
 * Cover label for tiles: SFRA `c_allVariantsData` sets `swatchName` from variation `displayValue` (prefers `cover`).
 * Prefer {@link getCoverVariantProductName} for shopper-facing selection/footer labels.
 *
 * @param {Object} variant
 * @returns {string}
 */
export function getCoverAttributeDisplayNameForTile(variant) {
  return (
    variant?.swatchName ||
    variant?.c_processedData?.coverDisplayValue ||
    getCoverDisplayValueFromVariationValues(variant?.variationValues) ||
    variant?.name ||
    ''
  );
}

/** Cover variant catalog `name` only. */
export function getCoverVariantProductName(variant) {
  return trimOptionProductNameLine(variant?.name);
}

/**
 * PIM collection / material on a cover variant (DE-268 tile line 1).
 *
 * @param {Object|null|undefined} variant
 * @returns {string}
 */
export function getCoverMaterialName(variant) {
  const processed = variant?.c_processedData ?? {};

  return String(processed.materialName ?? variant?.c_materialName ?? '').trim();
}

/**
 * Add-to-cart strip cover label: `materialName + productName` (same join as View Details).
 * Footer / CTA still use {@link getCoverVariantProductName} only.
 *
 * @param {Object|null|undefined} cover - Committed cover selection
 * @returns {string}
 */
export function formatCoverStripSummaryLabel(cover) {
  if (!cover) return '';

  const material = typeof cover.materialName === 'string' ? cover.materialName.trim() : '';
  const name = typeof cover.name === 'string' ? cover.name.trim() : '';

  if (material && name && material !== name) return `${material} ${name}`;

  return name || material || '';
}

/**
 * Cover swatch URL — SFRA `c_allVariantsData[].swatchImageUrl`
 * (swatch → first `pimlyConfigurationImage` entry on variant).
 * No PWA fallback to processed image groups, master, PIM, or Cylindo.
 *
 * @param {Object} _product - Unused; kept for call-site compatibility
 * @param {Object} variant
 * @returns {string|null}
 */
export function getVariantSwatchImageUrl(_product, variant) {
  const url = variant?.swatchImageUrl;

  if (url != null && String(url).trim() !== '') {
    return String(url).trim();
  }

  return null;
}

/**
 * Overlay cover-tile `swatchImageUrl` with SCAPI `c_pimlyProductImage`.
 * Until the fetch finishes, keep the incoming URL. Missing SCAPI row keeps it too;
 * present product with no c_pimlyProductImage still becomes placeholder.
 *
 * @param {Array<Object>} variants
 * @param {Record<string, Object>} productsById
 * @param {boolean} hydrationComplete
 * @returns {Array<Object>}
 */
export function mergeCoverSwatchPimlyProductImages(variants, productsById, hydrationComplete) {
  if (!Array.isArray(variants)) return [];

  return variants.map((variant) => {
    if (!hydrationComplete) {
      return variant;
    }

    const id = variant?.productId ?? variant?.id;
    const fetched = id != null ? productsById?.[String(id)] : null;

    if (!fetched) {
      return variant;
    }

    return {
      ...variant,
      swatchImageUrl: resolveOptionSwatchThumbnailUrl(fetched),
    };
  });
}

/**
 * @param {*} raw
 * @returns {Array|null}
 */
function parseJsonAttribute(raw) {
  if (!raw) return null;

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * All cover variants (prefers `c_allVariantsData`; falls back to SCAPI `variants`, capped).
 *
 * @param {Object} product
 * @returns {Array}
 */
export const getAllCoverVariants = (product) => {
  if (!product) {
    return [];
  }

  const fromAllVariants = parseJsonAttribute(product.c_allVariantsData);

  if (fromAllVariants && fromAllVariants.length > 0) {
    return fromAllVariants;
  }

  return product.variants ?? [];
};

/**
 * Cover variants for PDP / flyout display: `popularCovers` first (DE-761/DE-780),
 * then remaining by price ascending → colour light→dark.
 *
 * @param {Object} product
 * @returns {Array}
 */
export const getSortedCoverVariants = (product) => {
  const variants = getAllCoverVariants(product);

  return sortCoverVariantsForDisplay(variants, getPopularCoverIds(product));
};

/**
 * Enriched cover variant for `coverId` from `c_allVariantsData` (or `variants` fallback).
 * @param {Object|null} product
 * @param {string|null} coverId
 */
export const getSelectedVariantProduct = (product, coverId) => {
  if (!product || !coverId) return null;

  const variants = getAllCoverVariants(product);
  const coverKey = String(coverId);

  return variants.find((v) => {
    const id = v?.productId ?? v?.id;

    return id != null && String(id) === coverKey;
  }) ?? null;
};

/**
 * @param {Array} variants
 * @param {Object|null} [product]
 * @returns {Array}
 */
function buildCoverGroupsFromVariants(variants, product = null) {
  if (!Array.isArray(variants) || variants.length === 0) {
    return [];
  }

  const byItemType = new Map();

  for (const v of variants) {
    const processed = v?.c_processedData;
    const itemType = processed?.itemType ?? 'Other';

    if (!byItemType.has(itemType)) {
      byItemType.set(itemType, {byColor: new Map(), byCollection: new Map()});
    }

    const group = byItemType.get(itemType);
    const variantEntry = {
      productId: v.productId,
      variationValues: v.variationValues,
      orderable: v.orderable,
      ats: typeof v.ats === 'number' ? v.ats : null,
      perpetual: v.perpetual === true,
      leadTime: typeof v.leadTime === 'number' ? v.leadTime : null,
      name: v.name,
      swatchName: getCoverAttributeDisplayNameForTile(v),
      swatchImageUrl: product ? getVariantSwatchImageUrl(product, v) : null,
      swatchDescription: '',
      salePrice: v.salePrice ?? null,
      price: v.price ?? null,
      cylindoFeature: v.cylindoFeature ?? null,
      c_kingGuardApplicable: v.c_kingGuardApplicable === true,
      c_processedData: processed,
    };

    const color = processed?.refinementColor;

    if (color) {
      if (!group.byColor.has(color)) {
        group.byColor.set(color, []);
      }

      group.byColor.get(color).push(variantEntry);
    }

    const collectionName = processed?.materialName;

    if (collectionName) {
      if (!group.byCollection.has(collectionName)) {
        group.byCollection.set(collectionName, []);
      }

      group.byCollection.get(collectionName).push(variantEntry);
    }
  }

  return Array.from(byItemType.entries()).map(([itemType, g]) => ({
    itemType,
    byColor: Array.from(g.byColor.entries()).map(([color, vs]) => ({color, variants: vs})),
    byCollection: Array.from(g.byCollection.entries()).map(([collectionName, vs]) => ({
      collectionName,
      variants: vs,
    })),
  }));
}

/**
 * Cover groups for the customize cover step: `c_coverGroupsData` legacy, else group from all variants.
 * Colour groups ordered light→dark; collections by min price; variants by price → colour (DE-780).
 *
 * @param {Object} product
 * @returns {Array}
 */
export const getCoverGroups = (product) => {
  if (!product) return [];

  const legacyGrouped = parseJsonAttribute(product.c_coverGroupsData);
  const groups =
    legacyGrouped && legacyGrouped.length > 0 ?
      legacyGrouped :
      buildCoverGroupsFromVariants(getAllCoverVariants(product), product);

  return groups.map((group) => ({
    ...group,
    byColor: sortColourGroups(group.byColor ?? []),
    byCollection: sortCollectionGroups(group.byCollection ?? []),
  }));
};

// --- Selection maps (draft / preselect / option slots) ------------------------

/**
 * Filters recommendation product entries for UI lists.
 * Legacy SFRA payloads may still carry per-product `isHidden`; slot visibility uses OGU `isVisible`.
 *
 * @param {Array<{isHidden?: boolean}>} [products]
 * @returns {Array}
 */
export function filterVisibleOptionProducts(products) {
  if (!Array.isArray(products)) return [];

  return products.filter((p) => p && p.isHidden !== true);
}

function isOguValueShopperInvisible(value) {
  return value?.isHidden === true || value?.isVisible === false;
}

function isOguRecProductOrderable(product) {
  if (!product?.productId) return false;
  if (product.isOrderable === false) return false;
  if (product.isOrderable === true) return true;

  return product.ats == null || Number(product.ats) > 0;
}

function listOguProductsWithId(products) {
  return Array.isArray(products) ? products.filter((p) => p?.productId) : [];
}

export function getOguSlotCatalogProducts(value) {
  const raw = listOguProductsWithId(value?.products);

  if (isOguValueShopperInvisible(value)) return raw;

  const visible = filterVisibleOptionProducts(value?.products);

  return visible.length ? visible : raw;
}

function pickFirstOrderableOguProduct(products, allowOosFallback) {
  const list = listOguProductsWithId(products);
  const orderable = list.find(isOguRecProductOrderable);

  return orderable ?? (allowOosFallback ? list[0] ?? null : null);
}

/**
 * Slot preselect: isPreSelected → first orderable (OOS fallback);
 * hidden+required → first orderable only; visible required + 1 SKU → that SKU.
 */
export function resolveOguSlotPreselectProductId(value) {
  const products = getOguSlotCatalogProducts(value);

  if (!products.length) return null;

  if (value?.isPreSelected === true) {
    return pickFirstOrderableOguProduct(products, true)?.productId ?? null;
  }

  if (value?.isRequired === true && isOguValueShopperInvisible(value)) {
    return pickFirstOrderableOguProduct(products, false)?.productId ?? null;
  }

  if (value?.isRequired === true && products.length === 1) {
    return products[0].productId;
  }

  return null;
}

function isShopperInvisibleOguSlot(container, slotDef) {
  return isOguValueShopperInvisible(container) || isOguValueShopperInvisible(slotDef);
}

export function attachShopperInvisibleFlags(container, slotDef, slot) {
  if (!slot?.productId || !isShopperInvisibleOguSlot(container, slotDef)) {
    return slot;
  }

  return {
    ...slot,
    isAutoSelected: true,
    isHidden: true,
  };
}

function buildOguSlotPreselectSelection(container, value, productId) {
  const products = getOguSlotCatalogProducts(value);
  const match = products.find((p) => p.productId === productId) ?? products[0];

  return attachShopperInvisibleFlags(container, value, {
    productId,
    name: match?.name || null,
    recommendationName: value.typeName || container.containerName || null,
    price: match?.price ?? null,
  });
}

/**
 * Fill empty OGU slots. `disablePreselect` skips visible BM preselect only;
 * hidden+required still fills.
 */
export function mergePreselectedSelectionsFromProduct(product, baseSelections, {disablePreselect = false} = {}) {
  if (!product) {
    return baseSelections && typeof baseSelections === 'object' ? {...baseSelections} : {};
  }

  const base = baseSelections && typeof baseSelections === 'object' ? baseSelections : {};
  const processed = getOptionsGroupingUIProcessed(product);

  if (!processed || !Array.isArray(processed)) {
    return {...base};
  }

  const out = {...base};

  for (const container of processed) {
    const cid = container?.containerId;

    if (!cid || cid === 'cover') continue;

    const slots = Array.isArray(container.values) ? container.values : [];
    const slotSelections = [...(out[cid] || [])];

    slots.forEach((value, idx) => {
      if (slotSelections[idx] != null) return;
      if (
        disablePreselect &&
        !(value?.isRequired === true && isOguValueShopperInvisible(value))
      ) {
        return;
      }

      const productId = resolveOguSlotPreselectProductId(value);

      if (productId) {
        slotSelections[idx] = buildOguSlotPreselectSelection(container, value, productId);
      }
    });

    out[cid] = slotSelections;
  }

  return out;
}

function normalizeSiblingKeyPart(value) {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function resolveSiblingNameKey(product, container) {
  return normalizeSiblingKeyPart(resolveOptionSlotSelectionName(product, container));
}

function resolveSiblingMatchKey(product, container) {
  const namePart = resolveOptionProductName(product);
  const descPart = resolveOptionProductShortDescriptionPlain(product);

  if (namePart && descPart) {
    return normalizeSiblingKeyPart(joinOptionProductNameAndShortDescription(namePart, descPart));
  }

  return resolveSiblingNameKey(product, container);
}

function enrichSiblingSourceFromOgu(sourceProduct, container, completedSlotIndex) {
  const productId = sourceProduct?.productId ?? sourceProduct?.id;

  if (!productId || !container?.values?.length) return sourceProduct;

  const oguMatch = filterVisibleOptionProducts(container.values[completedSlotIndex]?.products).find(
    (p) => p?.productId === productId
  );

  if (!oguMatch) return sourceProduct;

  return {
    ...sourceProduct,
    name: sourceProduct?.name ?? oguMatch.name ?? null,
    shortDescription: sourceProduct?.shortDescription ?? oguMatch.shortDescription ?? null,
  };
}

/**
 * Find a sibling-slot product matching the confirmed picker product.
 * Uses name + shortDescription when both exist; else formatted slot name.
 * Multiple OGU rows sharing the same match key → first visible row wins (KL-1120).
 *
 * @param {Array} visible
 * @param {Object} sourceProduct
 * @param {Object|null|undefined} container
 * @returns {Object|null}
 */
function findSiblingMatchProduct(visible, sourceProduct, container) {
  const sourceKey = resolveSiblingMatchKey(sourceProduct, container);

  if (!sourceKey) return null;

  const byKey = (Array.isArray(visible) ? visible : []).filter(
    (p) => p && resolveSiblingMatchKey(p, container) === sourceKey
  );

  if (byKey.length === 0) return null;

  return byKey[0];
}

/**
 * Auto-fills empty sibling slots with matching products when user confirms a slot.
 * Only applies to slots that are currently empty.
 *
 * @param {Object} container - OGU container (containerId, values[])
 * @param {Array} row - Current slot row
 * @param {number} completedSlotIndex - Slot index user just committed
 * @param {Object} sourceProduct - Product from picker (display name drives matching)
 * @returns {Array} New slot row array
 */
export function applySiblingSlotMatchesByDisplayName(
  container,
  row,
  completedSlotIndex,
  sourceProduct
) {
  if (!container?.values?.length) {
    return row;
  }

  const enrichedSource = enrichSiblingSourceFromOgu(sourceProduct, container, completedSlotIndex);

  if (!resolveSiblingMatchKey(enrichedSource, container)) {
    return row;
  }

  const {values} = container;
  const out = [...row];

  for (let j = 0; j < values.length; j++) {
    if (j === completedSlotIndex) continue;
    if (out[j]?.productId) continue;

    const slotValue = values[j];
    const visible = filterVisibleOptionProducts(slotValue?.products);
    const match = findSiblingMatchProduct(visible, enrichedSource, container);

    if (!match?.productId) continue;

    const stubProduct = {
      id: match.productId,
      imageGroups: [],
      name: match.name || enrichedSource?.name || '',
      shortDescription: match.shortDescription ?? enrichedSource?.shortDescription ?? null,
    };

    out[j] = buildOptionSlotSelectionFromProduct(stubProduct, j, slotValue, container);
  }

  return out;
}

/**
 * Sum cover + option slot prices for a predefined-set card.
 * Skips `optionalAddOns`. Includes hidden OGU slots with a productId.
 *
 * @param {Object|null} selections - SelectionsMap
 * @param {Object.<string, number|null|undefined>} [optionPriceMap] - productId → effective price
 * @returns {number}
 */
export function sumSetCardSelectionPrices(selections, optionPriceMap) {
  if (!selections || typeof selections !== 'object') return 0;

  let total = 0;

  const cover = selections.cover;

  if (cover) {
    // Prefer the promotion-aware effective price from the fetched map (SCAPI
    // productPromotions), falling back to the cover's sale/list price.
    const raw =
      cover.productId && optionPriceMap && optionPriceMap[cover.productId] != null ?
        optionPriceMap[cover.productId] :
        (cover.salePrice ?? cover.price);
    const n = typeof raw === 'number' ? raw : Number(raw);

    if (Number.isFinite(n)) total += n;
  }

  Object.entries(selections).forEach(([key, slots]) => {
    if (key === 'cover' || key === 'optionalAddOns' || !Array.isArray(slots)) return;

    slots.forEach((slot) => {
      if (!slot?.productId) return;

      let p =
        optionPriceMap && optionPriceMap[slot.productId] != null ?
          optionPriceMap[slot.productId] :
          slot?.price;

      if (p == null || !Number.isFinite(Number(p))) {
        p = slot?.price;
      }

      const num = typeof p === 'number' ? p : Number(p);

      if (Number.isFinite(num)) total += num;
    });
  });

  return total;
}

/**
 * Sum list prices (RRP / root price book) across cover + option slots for a set card.
 * Returns 0 when no list-price data exists (= no discount to show).
 *
 * @param {Object|null} selections - SelectionsMap
 * @param {Object.<string, number|null|undefined>} [listPriceMap] - productId → list price
 * @returns {number}
 */
export function sumSetCardSelectionListPrices(selections, listPriceMap) {
  if (!selections || typeof selections !== 'object') return 0;

  let total = 0;
  let hasListData = false;

  const cover = selections.cover;

  if (cover) {
    const listP = cover.price;
    const saleP = cover.salePrice;

    if (listP != null && saleP != null && listP > saleP) {
      total += listP;
      hasListData = true;
    } else {
      const raw = saleP ?? listP;
      const n = typeof raw === 'number' ? raw : Number(raw);

      if (Number.isFinite(n)) total += n;
    }
  }

  Object.entries(selections).forEach(([key, slots]) => {
    if (key === 'cover' || key === 'optionalAddOns' || !Array.isArray(slots)) return;

    slots.forEach((slot) => {
      if (!slot?.productId) return;

      let lp = slot?.listPrice;

      if (lp == null && listPriceMap && listPriceMap[slot.productId] != null) {
        lp = listPriceMap[slot.productId];
      }

      if (lp != null && lp > (slot?.price ?? 0)) {
        total += lp;
        hasListData = true;
      } else {
        let p = slot?.price;
        const num = typeof p === 'number' ? p : Number(p);

        if (Number.isFinite(num)) total += num;
      }
    });
  });

  return hasListData ? total : 0;
}

function trimOptionProductNameLine(value) {
  if (value == null) return '';
  const s = typeof value === 'string' ? value : String(value);

  return s.trim();
}

function joinOptionProductNameAndShortDescription(namePart, descPart) {
  if (namePart && descPart) {
    return `${namePart}, ${descPart}`;
  }

  return namePart || descPart || '';
}

/** Option label: `name, shortDescription` when both exist. */
export function formatOptionProductSelectionName(product) {
  if (!product) return '';

  return joinOptionProductNameAndShortDescription(
    resolveOptionProductName(product),
    resolveOptionProductShortDescriptionPlain(product)
  );
}

/** Committed option slot `selection.name` (shelves: catalog name only). */
export function resolveOptionSlotSelectionName(product, container) {
  if (isMultiTypeOptionsContainer(container)) {
    return resolveShelvesPrimaryName(product) ?? '';
  }

  return formatOptionProductSelectionName(product);
}

/**
 * Hero PIM carousel images for an option/recommendation product, mirroring
 * `buildLayerFromProduct` (gallery-resource-utils): the `large`, non-variation
 * group sourced from PIM. Returned so the committed slot can rebuild its gallery
 * layer (`buildLayersFromSelections`) for PIM-only options that carry no
 * `cylindoFeature` — without this the option image vanishes once the live-preview
 * product is cleared on flyout "Done" and the hero falls back to "Image not
 * available" (KINDW simple-PDP gallery revert).
 *
 * @param {Object|null|undefined} product
 * @returns {Array|null}
 */
function resolveOptionSlotPimImages(product) {
  const imageGroups = parseProcessedImageGroups(product);
  const heroGroup = imageGroups.find((g) => g.viewType === 'large' && !g.variationAttributes) ?? null;

  return heroGroup?.imageSource === 'pim' && heroGroup.images?.length > 0 ? heroGroup.images : null;
}

/**
 * Dedicated video URL for an option/recommendation product, mirroring
 * `getProductVideoUrl` (gallery-resource-utils). Stored on the committed slot so
 * the committed gallery layer keeps parity with the live-preview
 * `buildLayerFromProduct` — without this the option video vanishes once the
 * live-preview product is cleared on flyout "Done" (or after refresh) and the
 * hero falls back to the still image (KINDW simple-PDP video revert).
 *
 * Defined locally rather than imported from gallery-resource-utils, which already
 * imports from this module (avoids a circular dependency).
 *
 * @param {Object|null|undefined} product
 * @returns {string|null}
 */
function resolveOptionSlotVideoUrl(product) {
  const raw = product?.c_video ?? product?.video ?? null;
  const url = raw != null ? String(raw).trim() : '';

  return url || null;
}

/**
 * One committed option slot row (`selections[containerId][slotIndex]`).
 *
 * @param {Object} product
 * @param {number} slotIndex
 * @param {Object} value - OGU slot value (`typeName`, `recommendationTypeId`, …)
 * @param {Object} container
 * @returns {Object}
 */
export function buildOptionSlotSelectionFromProduct(product, slotIndex, value, container) {
  const imageUrl = resolveOptionSwatchThumbnailUrl(product);

  // cylindoFeature is stored so V2 gallery resource layering can combine features
  // from master + cover variant + all committed option slots without re-fetching products.
  const rawFeature = product?.c_cylindoFeature ?? product?.cylindoFeature ?? null;

  const effectivePrice = getProductEffectivePrice(product);
  const listPrice = getProductListPrice(product);

  return {
    imageUrl,
    name: resolveOptionSlotSelectionName(product, container),
    shortDescription: resolveOptionProductShortDescriptionPlain(product) || null,
    productId: product.id,
    price: effectivePrice,
    listPrice,
    recommendationName: value.typeName || container.containerName || null,
    recommendationTypeId: value.recommendationTypeId,
    slotIndex,
    cylindoFeature: normalizeCylindoFeatureToString(rawFeature),
    // PIM hero images so the committed gallery layer survives flyout close for
    // PIM-only options (parity with the live-preview `buildLayerFromProduct`).
    pimImages: resolveOptionSlotPimImages(product),
    // Dedicated video URL so a video option keeps showing in the hero after
    // "Done" and after refresh (video wins over PIM/Cylindo in the resolver).
    video: resolveOptionSlotVideoUrl(product),
    // Selection recency marker. When every slot is filled, the hero must reflect
    // the option the shopper most recently changed — not whichever slot happens to
    // sit last in catalog order. Layers are ordered by this in
    // buildLayersFromSelections so the latest choice wins the exclusive hero
    // (video/PIM last-layer-wins) and Cylindo feature merge (later wins per key).
    // Only re-stamped for the slot being (re)built, so untouched slots keep their
    // original recency across commits and refresh (persisted to session).
    selectedAt: Date.now(),
  };
}
